/**
 * Heir categories for the Faraid calculator
 * Organized in tiers for progressive disclosure in the UI
 *
 * Tier 1: Primary heirs (most common — always shown)
 * Tier 2: Extended heirs (expandable groups)
 *
 * Each entry:
 *   category    — matches backend HeirCategory constant
 *   labelKey    — i18n key for display
 *   gender      — "male" or "female" heir
 *   max         — max count allowed (null = unlimited counter)
 *   inputType   — "counter" (0..N) or "toggle" (0 or 1)
 *   deceasedGender — "male", "female", or "any" (which deceased genders show this heir)
 */

// Tier 1: Primary heirs — always visible
export const tier1Heirs = [
  // Spouse
  {
    category: 'husband',
    labelKey: 'heir.husband',
    gender: 'male',
    max: 1,
    inputType: 'toggle',
    deceasedGender: 'female',
  },
  {
    category: 'wife',
    labelKey: 'heir.wife',
    gender: 'female',
    max: 4,
    inputType: 'counter',
    deceasedGender: 'male',
  },
  // Children
  {
    category: 'son',
    labelKey: 'heir.son',
    gender: 'male',
    max: null,
    inputType: 'counter',
    deceasedGender: 'any',
  },
  {
    category: 'daughter',
    labelKey: 'heir.daughter',
    gender: 'female',
    max: null,
    inputType: 'counter',
    deceasedGender: 'any',
  },
  // Parents
  {
    category: 'father',
    labelKey: 'heir.father',
    gender: 'male',
    max: 1,
    inputType: 'toggle',
    deceasedGender: 'any',
  },
  {
    category: 'mother',
    labelKey: 'heir.mother',
    gender: 'female',
    max: 1,
    inputType: 'toggle',
    deceasedGender: 'any',
  },
]

// Tier 2: Extended heirs — shown in expandable groups
export const tier2Groups = [
  {
    groupKey: 'heir.group.grandchildren',
    labelKey: 'heir.grandchildren',
    heirs: [
      {
        category: 'sons_son',
        labelKey: 'heir.sonsSon',
        gender: 'male',
        max: null,
        inputType: 'counter',
        deceasedGender: 'any',
      },
      {
        category: 'sons_daughter',
        labelKey: 'heir.sonsDaughter',
        gender: 'female',
        max: null,
        inputType: 'counter',
        deceasedGender: 'any',
      },
    ],
  },
  {
    groupKey: 'heir.group.grandparents',
    labelKey: 'heir.grandparents',
    heirs: [
      {
        category: 'fathers_father',
        labelKey: 'heir.fathersFather',
        gender: 'male',
        max: 1,
        inputType: 'toggle',
        deceasedGender: 'any',
      },
      {
        category: 'paternal_grandmother',
        labelKey: 'heir.paternalGrandmother',
        gender: 'female',
        max: 1,
        inputType: 'toggle',
        deceasedGender: 'any',
      },
      {
        category: 'maternal_grandmother',
        labelKey: 'heir.maternalGrandmother',
        gender: 'female',
        max: 1,
        inputType: 'toggle',
        deceasedGender: 'any',
      },
    ],
  },
  {
    groupKey: 'heir.group.siblings',
    labelKey: 'heir.siblings',
    heirs: [
      {
        category: 'full_brother',
        labelKey: 'heir.fullBrother',
        gender: 'male',
        max: null,
        inputType: 'counter',
        deceasedGender: 'any',
      },
      {
        category: 'full_sister',
        labelKey: 'heir.fullSister',
        gender: 'female',
        max: null,
        inputType: 'counter',
        deceasedGender: 'any',
      },
      {
        category: 'paternal_half_brother',
        labelKey: 'heir.paternalHalfBrother',
        gender: 'male',
        max: null,
        inputType: 'counter',
        deceasedGender: 'any',
      },
      {
        category: 'paternal_half_sister',
        labelKey: 'heir.paternalHalfSister',
        gender: 'female',
        max: null,
        inputType: 'counter',
        deceasedGender: 'any',
      },
      {
        category: 'maternal_half_brother',
        labelKey: 'heir.maternalHalfBrother',
        gender: 'male',
        max: null,
        inputType: 'counter',
        deceasedGender: 'any',
      },
      {
        category: 'maternal_half_sister',
        labelKey: 'heir.maternalHalfSister',
        gender: 'female',
        max: null,
        inputType: 'counter',
        deceasedGender: 'any',
      },
    ],
  },
  {
    groupKey: 'heir.group.unclesCousins',
    labelKey: 'heir.unclesCousins',
    heirs: [
      {
        category: 'full_brothers_son',
        labelKey: 'heir.fullBrothersSon',
        gender: 'male',
        max: null,
        inputType: 'counter',
        deceasedGender: 'any',
      },
      {
        category: 'paternal_half_brothers_son',
        labelKey: 'heir.paternalHalfBrothersSon',
        gender: 'male',
        max: null,
        inputType: 'counter',
        deceasedGender: 'any',
      },
      {
        category: 'full_paternal_uncle',
        labelKey: 'heir.fullPaternalUncle',
        gender: 'male',
        max: null,
        inputType: 'counter',
        deceasedGender: 'any',
      },
      {
        category: 'paternal_half_uncle',
        labelKey: 'heir.paternalHalfUncle',
        gender: 'male',
        max: null,
        inputType: 'counter',
        deceasedGender: 'any',
      },
      {
        category: 'full_cousin',
        labelKey: 'heir.fullCousin',
        gender: 'male',
        max: null,
        inputType: 'counter',
        deceasedGender: 'any',
      },
      {
        category: 'paternal_half_cousin',
        labelKey: 'heir.paternalHalfCousin',
        gender: 'male',
        max: null,
        inputType: 'counter',
        deceasedGender: 'any',
      },
    ],
  },
]

/**
 * Get all heir definitions filtered by deceased gender
 */
export function getHeirsForGender(deceasedGender) {
  const filterByGender = (heir) =>
    heir.deceasedGender === 'any' || heir.deceasedGender === deceasedGender

  return {
    tier1: tier1Heirs.filter(filterByGender),
    tier2: tier2Groups
      .map((group) => ({
        ...group,
        heirs: group.heirs.filter(filterByGender),
      }))
      .filter((group) => group.heirs.length > 0),
  }
}
